import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, contactsAPI } from '../services/api';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Menu, QrCode, ChevronDown, Check, Archive, CalendarDays, UserRound, LogOut } from 'lucide-react';
import QRCodeModal from './QRCodeModal';
import useMyEvents from '../hooks/useMyEvents';
import { eventHomePath, EVENTS_LIST_PATH, isEventPast, describeRegistration, isRegistered } from '../config/events';
import './NavigationBar.css';

/**
 * The event this page belongs to: from the URL on /event/... routes, otherwise the
 * last visited event (profile/settings pages render that event's sidebar). None on the events list.
 */
const contextEventNameId = (pathname) => {
    const match = pathname.match(/^\/event\/([^/]+)/);
    if (match) return match[1];
    if (pathname === EVENTS_LIST_PATH) return null;
    try {
        return localStorage.getItem('last_event') || null;
    } catch (e) {
        return null;
    }
};

/** Hook that closes a popover when clicking outside of `ref` or pressing Escape. */
const useDismiss = (ref, open, onClose) => {
    useEffect(() => {
        if (!open) return undefined;
        const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', onPointer);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onPointer);
            document.removeEventListener('keydown', onKey);
        };
    }, [ref, open, onClose]);
};

/**
 * Current-event pill with a dropdown of every event the contact can see.
 * Rendered only inside an event route.
 */
const EventSwitcher = ({ currentEventNameId }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const { events, loading } = useMyEvents();
    useDismiss(ref, open, () => setOpen(false));

    const current = events.find((e) => e.EventNameId === currentEventNameId);
    const currentPast = current ? isEventPast(current) : false;
    const label = current?.Name || currentEventNameId;

    const choose = (event) => {
        setOpen(false);
        if (event.EventNameId !== currentEventNameId) navigate(eventHomePath(event.EventNameId));
    };

    return (
        <div className="nav-event-switcher" ref={ref}>
            <button
                type="button"
                className={`nav-event-btn ${open ? 'open' : ''}`}
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                title="Switch event"
            >
                <span className="nav-event-label">{label}</span>
                {currentPast && <span className="nav-event-archived"><Archive size={11} aria-hidden="true" /> Archived</span>}
                <ChevronDown size={14} className={`nav-chevron ${open ? 'open' : ''}`} aria-hidden="true" />
            </button>

            {open && (
                <div className="nav-popover nav-event-menu" role="listbox" aria-label="Your events">
                    <div className="nav-popover-title">Your events</div>
                    {loading && events.length === 0 && <div className="nav-popover-empty">Loading…</div>}
                    {!loading && events.length === 0 && <div className="nav-popover-empty">No events found.</div>}
                    {events.map((event) => {
                        const past = isEventPast(event);
                        const status = describeRegistration(event.registration);
                        const selected = event.EventNameId === currentEventNameId;
                        return (
                            <button
                                key={event.EventId}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                className={`nav-event-option ${selected ? 'selected' : ''} ${past ? 'past' : ''}`}
                                onClick={() => choose(event)}
                            >
                                <span className="nav-event-option-check" aria-hidden="true">
                                    {selected && <Check size={14} strokeWidth={3} />}
                                </span>
                                <span className="nav-event-option-body">
                                    <span className="nav-event-option-name">{event.Name}</span>
                                    <span className="nav-event-option-meta">
                                        {past ? 'Archived' : status.label}
                                        {!past && isRegistered(event.registration) && event.registration?.event_entity_name
                                            ? ` · ${event.registration.event_entity_name}` : ''}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                    <div className="nav-popover-divider" />
                    <Link to={EVENTS_LIST_PATH} className="nav-popover-link" onClick={() => setOpen(false)}>
                        <CalendarDays size={15} aria-hidden="true" /> All events &amp; registration
                    </Link>
                </div>
            )}
        </div>
    );
};

const NavigationBar = () => {
    const { user } = useAuth();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [mobileNavBadgeCount, setMobileNavBadgeCount] = useState(0);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrCodeLink, setQrCodeLink] = useState(null);
    const [loadingQRCode, setLoadingQRCode] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const currentEventNameId = contextEventNameId(location.pathname);
    const hasSidebar = !!currentEventNameId;

    useDismiss(dropdownRef, isDropdownOpen, () => setIsDropdownOpen(false));

    // Listen for badge count updates from EventNavigation
    useEffect(() => {
        const handleBadgeUpdate = (event) => {
            setMobileNavBadgeCount(event.detail?.count || 0);
        };
        window.addEventListener('mobileNavBadgeUpdate', handleBadgeUpdate);
        return () => window.removeEventListener('mobileNavBadgeUpdate', handleBadgeUpdate);
    }, []);

    // Toggle mobile navigation menu
    const toggleMobileNav = () => {
        window.dispatchEvent(new CustomEvent('toggleMobileNav'));
    };

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const response = await authAPI.getCurrentUser();
                setUserDetails(response.data);
            } catch (error) {
                console.error('Error fetching user details:', error);
                setUserDetails(user);
            }
        };

        if (user && !userDetails) {
            fetchUserDetails();
        }

        const handleProfileUpdate = () => {
            fetchUserDetails();
        };

        window.addEventListener('profileImageUpdated', handleProfileUpdate);
        return () => window.removeEventListener('profileImageUpdated', handleProfileUpdate);
    }, [user, userDetails]);

    const handleLogout = () => {
        // Navigate to the logout page which clears cache, localStorage, cookies
        navigate('/logout');
    };

    const handleLogoClick = () => {
        navigate(currentEventNameId ? eventHomePath(currentEventNameId) : EVENTS_LIST_PATH);
    };

    // Get initials from first and last name
    const getInitials = () => {
        if (!userDetails) return '?';
        const firstName = userDetails.first_name || '';
        const lastName = userDetails.last_name || '';
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
    };

    const getDisplayName = () => {
        if (!userDetails) return 'User';
        return userDetails.first_name || userDetails.email || 'User';
    };

    const registrationFallbackLink = () => {
        if (currentEventNameId) {
            return `${window.location.origin}/event/${currentEventNameId}/registration/select`;
        }
        return window.location.href;
    };

    const fetchQRCodeLink = async () => {
        if (qrCodeLink) return qrCodeLink; // Return cached link

        setLoadingQRCode(true);
        try {
            const response = await contactsAPI.getMyQRCodeLink();
            if (response.data?.success && response.data?.qr_code_link) {
                setQrCodeLink(response.data.qr_code_link);
                return response.data.qr_code_link;
            }
            return registrationFallbackLink();
        } catch (error) {
            console.error('Error fetching QR code link:', error);
            return registrationFallbackLink();
        } finally {
            setLoadingQRCode(false);
        }
    };

    const handleQRCodeClick = async () => {
        setIsDropdownOpen(false);
        const link = await fetchQRCodeLink();
        setQrCodeLink(link);
        setQrModalOpen(true);
    };

    return (
        <>
            {/* QR Code Modal */}
            <QRCodeModal
                isOpen={qrModalOpen}
                onClose={() => setQrModalOpen(false)}
                url={qrCodeLink || window.location.href}
                title="Event Ticket"
            />

            <nav className="navigation-bar" aria-label="Primary">
                <div className="nav-container">
                    {/* Left - Hamburger (mobile, event pages only) + Logo + event switcher */}
                    <div className="nav-left">
                        {hasSidebar && (
                            <button
                                className="mobile-hamburger-btn"
                                onClick={toggleMobileNav}
                                aria-label="Open menu"
                            >
                                <Menu size={22} />
                                {mobileNavBadgeCount > 0 && (
                                    <span className="mobile-hamburger-badge">
                                        {mobileNavBadgeCount > 99 ? '99+' : mobileNavBadgeCount}
                                    </span>
                                )}
                            </button>
                        )}
                        <button type="button" className="nav-logo-btn" onClick={handleLogoClick} aria-label="Funds4Charity">
                            <img src="/Funds4Charity.png" alt="" className="nav-logo" />
                        </button>
                        {currentEventNameId ? (
                            <>
                                <span className="nav-sep" aria-hidden="true" />
                                <EventSwitcher currentEventNameId={currentEventNameId} />
                            </>
                        ) : (
                            <>
                                <span className="nav-sep" aria-hidden="true" />
                                <span className="nav-crumb">Your events</span>
                            </>
                        )}
                    </div>

                    {/* Right - QR Code Button + User Profile */}
                    <div className="nav-right">
                        <button
                            className="nav-icon-btn"
                            onClick={handleQRCodeClick}
                            disabled={loadingQRCode}
                            aria-label="Show event ticket QR code"
                            title="Event Ticket"
                        >
                            <QrCode size={19} />
                        </button>

                        <div className="user-profile" ref={dropdownRef}>
                            <button
                                type="button"
                                className={`user-profile-btn ${isDropdownOpen ? 'open' : ''}`}
                                onClick={() => setIsDropdownOpen((v) => !v)}
                                aria-haspopup="menu"
                                aria-expanded={isDropdownOpen}
                            >
                                <span className="user-avatar">
                                    {userDetails?.image_url ? (
                                        <img src={userDetails.image_url} alt="" className="avatar-image" />
                                    ) : (
                                        getInitials()
                                    )}
                                </span>
                                <span className="user-name">{getDisplayName()}</span>
                                <ChevronDown size={14} className={`nav-chevron ${isDropdownOpen ? 'open' : ''}`} aria-hidden="true" />
                            </button>

                            {isDropdownOpen && (
                                <div className="nav-popover dropdown-menu" role="menu">
                                    <div className="dropdown-header">
                                        <div className="dropdown-name">{userDetails?.full_name || getDisplayName()}</div>
                                        <div className="dropdown-email">{userDetails?.email}</div>
                                    </div>
                                    <div className="nav-popover-divider" />
                                    <button
                                        className="dropdown-item"
                                        role="menuitem"
                                        onClick={handleQRCodeClick}
                                        disabled={loadingQRCode}
                                    >
                                        <QrCode size={16} />
                                        {loadingQRCode ? 'Loading…' : 'Event ticket'}
                                    </button>
                                    <button
                                        className="dropdown-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setIsDropdownOpen(false);
                                            navigate(`/user/${userDetails?.contact_id}`);
                                        }}
                                    >
                                        <UserRound size={16} />
                                        My profile
                                    </button>
                                    <button
                                        className="dropdown-item"
                                        role="menuitem"
                                        onClick={() => {
                                            setIsDropdownOpen(false);
                                            navigate(EVENTS_LIST_PATH);
                                        }}
                                    >
                                        <CalendarDays size={16} />
                                        My events
                                    </button>
                                    <div className="nav-popover-divider" />
                                    <button className="dropdown-item logout" role="menuitem" onClick={handleLogout}>
                                        <LogOut size={16} />
                                        Log out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </nav>
        </>
    );
};

export default NavigationBar;
